import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createChart, IChartApi } from 'lightweight-charts';
import cx from 'classnames';

import { prettyPrice } from 'app/utils/helpers';
import Select from 'app/components/ui/Select/Select';
import Preloader from 'app/components/common/Preloader';
import GraphTooltip from '../GraphTooltip';
import {
  CandleGraphSeries,
  CommonGraphOptions,
  GraphicHeights,
  GraphicTooltipConfigs,
  GraphicTypes,
  HistogramGraphSeries,
  LinearGraphSeries,
} from '../types';

import s from './Graphic.module.sass';
import { CurrencyEnum } from '../../ui/CurrencySwitcher/CurrencySwitcher';
import { useCurrency } from '../../../context/CurrencyContext';
import { ReactComponent as Tezos } from '../../svg/Tezos.svg';

const usePrevious = (value: any) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const GraphInner: React.FC<GraphicProps & { data: any[], autoHeight: boolean }> = ({
  title,
  tooltipTitle,
  theme,
  height = GraphicHeights.default,
  autoHeight,
  data,
}) => {
  const { currency } = useCurrency();
  const isUSD = currency === CurrencyEnum.USD;

  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi>();
  const [value, setValue] = useState<any>();
  const prevThemeState = usePrevious(theme);
  const prevCurrencyState = usePrevious(isUSD);

  const formattedData = useMemo(() => {
    if (isUSD) {
      return data.map((el, index) => (theme === GraphicTypes.Candlestick
        ? el.xtzUsdQuoteHistorical !== 0 && ({
          time: el.time,
          open: el.open * (index === 0
            ? el.xtzUsdQuoteHistorical
            : data[index - 1].xtzUsdQuoteHistorical
          ),
          close: el.close * el.xtzUsdQuoteHistorical,
          high: el.high * el.xtzUsdQuoteHistorical,
          low: el.low * el.xtzUsdQuoteHistorical,
        })
        : el.xtzUsdQuoteHistorical !== 0 && ({
          time: el.time,
          value: el.value * el.xtzUsdQuoteHistorical,
        })));
    }
    return data;
  }, [data, isUSD, theme]);
  const prevDataState = usePrevious(formattedData[formattedData.length - 1]);

  // For reseting value on hover exit
  const currentValue = formattedData[formattedData.length - 1]
    ? (
      formattedData[formattedData.length - 1].value
      || formattedData[formattedData.length - 1].close
    )
    : (
      formattedData[0].value
      || formattedData[0].close
    );

  // Handle resize
  const handleResize = useCallback(() => {
    if (chartRef.current && chartWrapperRef.current?.parentElement) {
      const finalHeight = autoHeight
        ? Math.max(chartWrapperRef.current.parentElement.clientHeight, height)
        : height;
      chartRef.current.resize(chartWrapperRef.current.parentElement.clientWidth, finalHeight);
      chartRef.current.timeScale().fitContent();
      chartRef.current.timeScale().scrollToPosition(0, false);
    }
  }, [autoHeight, height]);

  // add event listener for resize
  const isClient = typeof window === 'object';
  useEffect(() => {
    if (!isClient) {
      return () => {};
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isClient, chartRef, handleResize]);

  // if chart not instantiated in canvas, create it
  useEffect(() => {
    if (
      prevThemeState !== theme
      || prevCurrencyState !== isUSD
      || prevDataState !== formattedData[formattedData.length - 1]
    ) {
      chartRef.current?.remove();
      chartRef.current = undefined;
    }
    if (
      !chartRef.current
      && formattedData
      && !!chartWrapperRef?.current?.parentElement
    ) {
      const finalHeight = autoHeight
        ? Math.max(chartWrapperRef.current.parentElement.clientHeight, height)
        : height;
      chartRef.current = createChart(chartWrapperRef.current, {
        height: finalHeight,
        width: chartWrapperRef.current.parentElement.clientWidth,
        ...CommonGraphOptions,
        localization: {
          dateFormat: 'MMM dd',
          locale: 'en',
          priceFormatter: (price: number) => `${isUSD ? '$ ' : ''}${prettyPrice(price!, 3)}${isUSD ? '' : ' XTZ'}`,
        },
      });

      // eslint-disable-next-line no-nested-ternary
      const series = theme === GraphicTypes.Candlestick
        ? chartRef.current.addCandlestickSeries(CandleGraphSeries)
        : (
          theme === GraphicTypes.Histogram
            ? chartRef.current.addHistogramSeries(HistogramGraphSeries)
            : chartRef.current.addAreaSeries(LinearGraphSeries)
        );

      series.setData(formattedData);

      // update the title when hovering on the chart
      chartRef.current.subscribeCrosshairMove((param) => {
        if (
          !chartWrapperRef?.current
          || param === undefined
          || param.time === undefined
          || param.point === undefined
          || param.point.x < 0
          || param.point.x > chartWrapperRef.current.clientWidth
          || param.point.y < 0
          || param.point.y > finalHeight
        ) {
          setValue(currentValue);
        } else {
          const price = theme === GraphicTypes.Candlestick
            // @ts-ignore
            ? parseFloat(param.seriesPrices.get(series)?.close.toString() ?? currentValue)
            : parseFloat(param.seriesPrices.get(series)?.toString() ?? currentValue);
          const coordinate = series.priceToCoordinate(
            theme === GraphicTypes.Candlestick
              ? Math.max(
                // @ts-ignore
                param.seriesPrices.get(series)?.open, param.seriesPrices.get(series)?.close,
              ) : price,
          );

          let shiftedCoordinate = param.point.x - GraphicTooltipConfigs.width / 2;
          if (!tooltipRef?.current || coordinate === null) {
            return;
          }

          shiftedCoordinate = Math.max(
            0,
            Math.min(
              chartWrapperRef?.current.clientWidth - GraphicTooltipConfigs.width,
              shiftedCoordinate,
            ),
          );

          const coordinateY = (
            coordinate - GraphicTooltipConfigs.height - GraphicTooltipConfigs.margin > 0
          )
            ? coordinate - GraphicTooltipConfigs.height - GraphicTooltipConfigs.margin
            : Math.max(
              0,
              Math.min(
                chartWrapperRef?.current.clientHeight
                - GraphicTooltipConfigs.height
                - GraphicTooltipConfigs.margin,
                coordinate + GraphicTooltipConfigs.margin,
              ),
            );

          tooltipRef.current.style.left = `${shiftedCoordinate}px`;
          tooltipRef.current.style.top = `${coordinateY}px`;

          setValue({
            price: (
              <>
                {isUSD ? '$' : <Tezos className={cx(s.tezosTooltip)} />}
                {' '}
                {prettyPrice(price, 3)}
              </>
            ),
            time: new Intl.DateTimeFormat(
              'en',
              theme === GraphicTypes.Candlestick ? {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              } : {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              },
            ).format(new Date(+param.time * 1000)),
          });
        }
      });

      if (theme === GraphicTypes.Candlestick) {
        chartRef.current.timeScale();
      } else {
        chartRef.current.timeScale().fitContent();
      }
    }
  }, [
    setValue,
    formattedData,
    height,
    theme,
    prevThemeState,
    currentValue,
    autoHeight,
    isUSD,
    prevCurrencyState,
    prevDataState,
  ]);

  return (
    <div className={s.chart}>
      <div ref={chartWrapperRef} />
      <GraphTooltip
        title={tooltipTitle || title}
        time={value?.time}
        price={value?.price}
        ref={tooltipRef}
      />
    </div>
  );
};

// Graph
type GraphicProps = {
  title?: string
  tooltipTitle?: string
  data?: any[]
  loading?: boolean
  height?: number
  theme?: GraphicTypes
  className?: string
  preloaderClassName?: string
  selectItems?: { id: number, type: string }[]
  selectActive?: string
  setSelectActive?: (el: any) => void
};

const Graphic: React.FC<GraphicProps> = ({
  title,
  tooltipTitle,
  data,
  loading,
  height,
  theme = GraphicTypes.Linear,
  className,
  preloaderClassName,
  selectItems,
  selectActive,
  setSelectActive,
}) => (
  <div className={cx(s.root, className)}>
    <div className={s.heading}>
      {selectItems && setSelectActive && (
      <Select
        items={selectItems}
        activeItem={selectActive}
        setActiveItem={(el) => setSelectActive(el)}
        theme="secondary"
        className={s.select}
      />
      )}
      {title && (
      <p className={cx(s.title)}>{title}</p>
      )}
    </div>
    {loading || !data || data.length === 0
      ? (
        <Preloader style={{ minHeight: height || '100%' }} className={preloaderClassName} />
      )
      : (
        <GraphInner
          data={data}
          title={title}
          tooltipTitle={tooltipTitle}
          height={height}
          autoHeight={!height}
          theme={theme}
        />
      )}
  </div>
);

export default Graphic;
